<script lang="ts">
    import confetti from 'canvas-confetti';
    import { clickOutside } from "$/utils/clickOutside";
    import { portal } from "$/utils/portal";
    import { checkIfExtensionInstalled } from "$/utils/Utils";
    import { slide } from "svelte/transition";
    import { IntroModalStore } from "./IntroModalStore";

    let showModal: boolean = window.location.hash.indexOf("intro") !== -1;
    let isLoading: boolean = true;

    // Periodically check if extension is installed
    let isExtensionInstalled: boolean = false;
    let isExtensionInstalledInterval = setInterval(async () => {
        isExtensionInstalled = await checkIfExtensionInstalled();
        isLoading = false;

        if (isExtensionInstalled) {
            clearInterval(isExtensionInstalledInterval);
        }
    }, 1000);

    let closeModal = () => {
        showModal = false;
        window.location.hash = "";
    };

    if (showModal) {
        confetti({
            particleCount: 100,
            spread: 100,
            origin: { y: 0.6 },
        });
    }
</script>

{#if showModal}
    <div
        class="absolute inset-0 z-max flex h-screen w-screen items-center justify-center bg-black bg-opacity-15 p-2"
        use:portal
    >
        <div
            class="flex w-full max-w-md flex-col gap-4 rounded-2xl bg-white p-6"
            transition:slide
            use:clickOutside
            on:click_outside={closeModal}
        >
            <h1 class="text-2xl font-bold text-black">🎉 Congrats!</h1>
            <div class="flex flex-col gap-2">
                <p>You've just made your #1 snippet!</p>
                <p>
                    As easy as that was, you can now start adding snippets from
                    different
                    <a
                        href="https://help.collabwriting.com/en/articles/7118584-getting-started-with-collabwriting"
                        target="_blank"
                        rel="noreferrer"
                    >
                        websites
                    </a>,
                    <a
                        href="https://help.collabwriting.com/en/articles/7835912-how-to-open-an-online-pdf-in-collabwriting"
                        target="_blank"
                        rel="noreferrer"
                    >
                        pdfs
                    </a>,
                    <a
                        href="https://help.collabwriting.com/en/articles/8057304-how-to-import-highlights-from-kindle"
                        target="_blank"
                        rel="noreferrer"
                    >
                        kindle books
                    </a>,
                    <a
                        href="https://help.collabwriting.com/en/articles/8680057-youtube-capture"
                        target="_blank"
                        rel="noreferrer"
                    >
                        YouTube videos
                    </a>
                    or
                    <a
                        href="https://help.collabwriting.com/en/articles/8519615-how-to-save-snippets-from-linkedin-posts"
                        target="_blank"
                        rel="noreferrer"
                    >
                        social media
                    </a> 🥳
                </p>
            </div>
            {#if !isLoading}
                <div transition:slide|local>
                    {#each [{ name: "Create first Topic", checked: true }, { name: "Add a Snippet", checked: true }, { name: "Install Extension", checked: isExtensionInstalled }, { name: "Continue Researching", checked: false }] as task}
                        <div
                            class="flex cursor-not-allowed select-none items-center gap-2"
                        >
                            {#if task.checked}
                                <svg
                                    class="size-6 text-sky-500"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                            {:else}
                                <svg
                                    class="size-6 text-black"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke-width="1.5"
                                    stroke="currentColor"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M21 12C21 13.1819 20.7672 14.3522 20.3149 15.4442C19.8626 16.5361 19.1997 17.5282 18.364 18.364C17.5282 19.1997 16.5361 19.8626 15.4442 20.3149C14.3522 20.7672 13.1819 21 12 21C10.8181 21 9.64778 20.7672 8.55585 20.3149C7.46392 19.8626 6.47177 19.1997 5.63604 18.364C4.80031 17.5282 4.13738 16.5361 3.68508 15.4442C3.23279 14.3522 3 13.1819 3 12C3 9.61305 3.94821 7.32387 5.63604 5.63604C7.32387 3.94821 9.61305 3 12 3C14.3869 3 16.6761 3.94821 18.364 5.63604C20.0518 7.32387 21 9.61305 21 12Z"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            {/if}

                            <p class="text-black">
                                {task.name}
                            </p>
                        </div>
                    {/each}

                    {#if isExtensionInstalled && $IntroModalStore?.suggestions?.length > 0}
                        <div
                            class="ml-8 mt-2 flex flex-col gap-2 rounded-b-xl rounded-tr-xl border border-gray-200 bg-gray-100 px-4 pb-4 pt-2"
                        >
                            <div class="text-sm text-gray-500">
                                Try these articles
                            </div>

                            <!-- show first 5 snippets -->
                            {#each $IntroModalStore.suggestions.slice(0, Math.min($IntroModalStore.suggestions.length, 5)) as snippet}
                                <a
                                    class="group flex items-center gap-2 rounded-full border-gray-200 bg-white px-4 py-1 text-black no-underline"
                                    href={snippet.url}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <span
                                        class="truncate text-sm text-black no-underline"
                                    >
                                        {snippet.nameSuggestion}
                                    </span>

                                    <svg
                                        class="ml-auto size-4 min-w-max text-black transition-all group-hover:-mr-2"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke-width="1.5"
                                        stroke="currentColor"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                                        />
                                    </svg>
                                </a>
                            {/each}
                        </div>
                    {/if}
                </div>
            {/if}

            {#if !isExtensionInstalled && !isLoading}
                <!-- TODO: Handle mobile phones -->
                <div
                    class="mt-2 flex flex-col items-center justify-center gap-4"
                    transition:slide|local
                >
                    <a
                        class="w-full cursor-pointer rounded-full bg-pink-500 px-4 py-2 text-center text-xl text-white transition-all hover:scale-105 hover:bg-pink-700"
                        href="https://collabwriting.com/extension"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Install Extension
                    </a>
                </div>
            {/if}
        </div>
    </div>
{/if}

<style>
    p {
        @apply text-lg text-black;
    }

    p a {
        @apply font-bold text-sky-500 underline hover:text-sky-700;
    }
</style>
